import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { label: '首页', path: '/' },
  { label: '角色百科', path: '/characters' },
  { label: '声优表', path: '/seiyuu' },
  { label: '作品专题', path: '/works' },
  { label: '关于我们', path: '/about' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/characters?q=${encodeURIComponent(q)}` : '/characters');
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-50/95 backdrop-blur shadow-sm border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-16 md:h-18 items-center justify-between gap-4">
          {/* 品牌 Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-leaf-fill text-lg" />
            </span>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap">
              妖精动漫网
            </span>
          </Link>

          {/* 桌面导航 */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const active = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-3.5 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                    active
                      ? 'text-primary-700 bg-primary-100/70 font-medium'
                      : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* 桌面搜索 */}
          <form onSubmit={handleSearch} className="hidden lg:flex items-center gap-2 rounded-full bg-background-100 border border-background-200/70 pl-3.5 pr-1.5 py-1.5">
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜角色 / 作品 / 声优…"
              className="w-36 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              aria-label="搜索角色、作品或声优"
            />
            <button type="submit" className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors" aria-label="搜索">
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </form>

          {/* 移动端按钮 */}
          <div className="md:hidden flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 transition-colors"
              aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
            >
              <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      {menuOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 pb-5 pt-3">
          <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2 rounded-full bg-background-100 border border-background-200/70 px-3.5 py-2">
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜角色 / 作品 / 声优…"
              className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              aria-label="搜索角色、作品或声优"
            />
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`py-2.5 px-2 rounded-md text-sm ${
                  location.pathname === item.path ? 'text-primary-700 bg-primary-100/70 font-medium' : 'text-foreground-700'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}